import {useEffect, useState} from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { tableCustomStyles } from "../../../components/CommonComponents/Table/Table";
import {PaginatorForRDT} from "../../../components/CommonComponents/Paginator/Paginator";
import { formatDate } from "../../../utils/date";
import {
    useCompletedApplicationsReport,
    TCompletedApplication,
    TCompletedReportFilters,
} from "../../../hooks/completedApplicationsReportHooks";
import { _axios } from "../../../utils/axios";
import PageHeader from "../../../components/pageHeader/pageHeader";
import SortIcon from "../../../components/CommonComponents/SortIcon/SortIcon";
import BoxComponent from "../../../components/CommonComponents/BoxComponent/BoxComponent";

// ── Column definitions — add/remove here only ────────────────────────────────
const COLUMNS: TableColumn<TCompletedApplication>[] = [
    { name: "Sr. No.",          selector: (r) => r.srNo,                                                          center: true, width: "200px",},
    { name: "Application ID",   selector: (r) => r.applicationId,  sortable: true, sortField: "applicationId",      center: true, width: "200px",},
    { name: "Plot Owner Name", selector: (r) => r.plotOwnerName, sortable: true,   sortField: "plotOwnerName",      center: true, width: "300px",},
    { name: "Plot No.",         selector: (r) => r.plotNo,          sortable: true, sortField: "plotNo",            center: true, width: "200px", },
    { name: "Mauja",            selector: (r) => r.mauja,           sortable: true, sortField: "mauja",             center: true, width: "200px",},
    { name: "Khasara No.",      selector: (r) => r.khasaraNo,       sortable: true, sortField: "khasaraNo",         center: true, width: "200px",},
    { name: "Plot Usage Type",  selector: (r) => r.plotUsageType,   sortable: true, sortField: "plotUsageType",     center: true, width: "200px",},
    //{ name: "Completed Date",   selector: (r) => formatDate(r.completedDate), sortable: true, sortField: "completedDate" },
    //{ name: "Receipt No",       selector: (r) => r.receiptNo,       sortable: true, sortField: "receiptNo" },
    { name: "Payment Receipt  Date",     selector: (r) => r.paymentDate, cell: (r) => formatDate(r.paymentDate),   sortable: true, sortField: "paymentDate", center: true, width: "300px",},
    { name: "Payment Amount",    selector: (r) => r.totalPayment,    sortable: true, sortField: "totalPayment", center: true,},
];

const PLOT_USAGE_OPTIONS = ["Residential", "Commercial", "Industrial"];
const DEFAULT_LIMIT       = 10;

// ── Component ─────────────────────────────────────────────────────────────────
export function CompletedApplicationsReport() {
    const [page,    setPage]    = useState(1);
    const [filters, setFilters] = useState<TCompletedReportFilters>({
        fromDate:      "",
        toDate:        "",
        plotUsageType: "",
    });
    const [appliedFilters, setAppliedFilters] = useState<TCompletedReportFilters | null>(null);
    const [{ sortKey, sortOrder }, setSort] = useState({
        sortKey:  "",
        sortOrder: -1,
    });

    // Only fetch once user hits "Search"
    const filtersReady = !!(appliedFilters?.fromDate && appliedFilters?.toDate && appliedFilters?.plotUsageType);

    const { isLoading, data } = useCompletedApplicationsReport({
        page,
        limit:   DEFAULT_LIMIT,
        filters: appliedFilters!,
        enabled: filtersReady,
        sort:     sortKey,
        sortVal:  sortOrder,
    });

    const [searchQuery, setSearchQuery] = useState("");

    function handleSearch() {
        setPage(1);
        setAppliedFilters({ ...filters });
    }

    useEffect(() => {
        console.log("📊 DataTable Data:", data?.data);
        console.log("📄 Full API Response:", data);
    }, [data]);

    async function handleExport() {
        try {
            const response = await _axios.post(
                "/completed-applications-report/export",
                {
                    fromDate: appliedFilters?.fromDate,
                    toDate: appliedFilters?.toDate,
                    plotUsageType: appliedFilters?.plotUsageType,
                },
                {
                    responseType: "blob", // 🔥 important
                }
            );

            // Create blob
            const blob = new Blob([response.data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });

            // Extract filename (optional but good)
            const contentDisposition = response.headers["content-disposition"];
            let fileName = "report.xlsx";

            if (contentDisposition) {
                const match = contentDisposition.match(/filename="(.+)"/);
                if (match?.[1]) fileName = match[1];
            }

            // Download
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();

        } catch (error) {
            console.error("Export failed", error);
        }
    }

    return (
        <div className="p-4 md:p-8 overflow-auto">
                        <PageHeader
                            title="Payment Reports"
                            searchQuery={searchQuery}
                            setSearchQuery={setSearchQuery}
                            showSearchButton={false}
                            showGridButton={false}
                            showFilterButton={false}
                            showAdditionalButton={false}
                            buttonTitle={""}
                        />
            {/* ── Filters ── */}
            <div className="flex flex-wrap gap-3 mb-4 items-center">
                <input
                    type="date"
                    value={filters.fromDate}
                    onChange={(e) =>
                        setFilters((f) => ({ ...f, fromDate: e.target.value }))
                    }
                    className="flex-1 min-w-[200px] py-3 px-4 rounded-lg bg-white disabled:bg-gray-200 border border-gray-400 pr-3"
                    style={{ fontSize: '16px' }}
            />
                <input
                    type="date"
                    value={filters.toDate}
                    onChange={(e) =>
                        setFilters((f) => ({ ...f, toDate: e.target.value }))
                    }
                    className="flex-1 min-w-[200px] py-3 px-4 rounded-lg bg-white disabled:bg-gray-200 border border-gray-400 pr-3"
                    style={{ fontSize: '16px' }}
                />

                <select
                    value={filters.plotUsageType}
                    onChange={(e) =>
                        setFilters((f) => ({ ...f, plotUsageType: e.target.value }))
                    }
                    className="flex-1 min-w-[200px] py-3.5 px-4 rounded-lg bg-white disabled:bg-gray-200 border border-gray-400 pr-3"
                    style={{ fontSize: '16px' }}
                >
                    <option value="">Select Plot Usage Type</option>
                    {PLOT_USAGE_OPTIONS.map((o) => (
                        <option key={o} value={o}>
                            {o}
                        </option>
                    ))}
                </select>

                <button
                    onClick={handleSearch}
                    className="whitespace-nowrap bg-primary py-3 px-4 sm:px-6 py-2 rounded-full font-bold text-white"
                >
                    Generate Report
                </button>

                {filtersReady && (
                    <button
                        onClick={handleExport}
                        className="whitespace-nowrap bg-blue-600 py-3 px-4 sm:px-6 py-2 rounded-full text-white font-bold disabled:bg-gray-300"
                    >
                        Download Report
                    </button>
                )}
            </div>

            {filtersReady && data?.totalPayment !== undefined && (
                <div className="flex flex-wrap gap-3 mb-4 items-center">
                    <div className="flex items-center gap-2 flex-1 min-w-[200px] max-w-[400px]">
                        <label className="whitespace-nowrap font-semibold text-gray-700">
                            Total Payment Received:
                        </label>
                        <input
                            type="text"
                            readOnly
                            value={`₹${data.totalPayment.toLocaleString("en-IN")}`}
                            className="flex-1 min-w-[100px] py-3 px-4 rounded-lg bg-gray-50 border border-gray-400 rounded-lg pr-3 font-semibold text-gray-800 cursor-default"
                        />
                    </div>
                </div>
            )}

            {/* ── Table ── */}
            <BoxComponent>
            <DataTable
                pagination
                paginationServer
                className="mt-4 rounded-0"
                columns={COLUMNS}
                data={data?.data ?? []}
                onChangePage={(p) => setPage(p)}
                paginationTotalRows={data?.pagination?.total ?? 0}
                progressPending={isLoading}
                customStyles={tableCustomStyles}
                sortIcon={<SortIcon />}
                noDataComponent={filtersReady ? "No data found" : "Apply filters to load report"}
                paginationComponent={PaginatorForRDT}
                onSort={(col, dir) => {
                    setPage(1);
                    setSort({
                        sortKey:   col.sortField || "completedDate",
                        sortOrder: dir === "asc" ? 1 : -1,
                    });
                }}
            />
            </BoxComponent>
        </div>
    );
}
